import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CheckCircle, ChevronRight, ChevronLeft, User, FileText, FolderOpen, Smartphone, Flag, AlertCircle, ExternalLink } from 'lucide-react'
import api from '../lib/api'

const CCN_LEVELS = [
  { value: 'E1', label: 'E1 — Employé niv. 1', rate: 12.063 },
  { value: 'E2', label: 'E2 — Employé niv. 2', rate: 12.134 },
  { value: 'E3', label: 'E3 — Employé niv. 3', rate: 12.147 },
  { value: 'E4', label: 'E4 — Employé niv. 4', rate: 12.400 },
  { value: 'E5', label: 'E5 — Employé niv. 5', rate: 12.486 },
  { value: 'E6', label: 'E6 — Employé niv. 6', rate: 12.785 },
  { value: 'E7', label: 'E7 — Employé niv. 7', rate: 12.882 },
  { value: 'AM1', label: 'AM1 — Agent de maîtrise 1', rate: 16.023 },
  { value: 'AM2', label: 'AM2 — Agent de maîtrise 2', rate: 16.296 },
  { value: 'C1', label: 'C1 — Cadre 1', rate: 19.929 },
  { value: 'C2', label: 'C2 — Cadre 2', rate: 22.077 },
]

const CONTRACT_TYPES = ['CDI', 'CDD', 'Extra', 'Stage', 'Intérim']

const STEPS = [
  { id: 1, label: 'Identité',     icon: User },
  { id: 2, label: 'Contrat',      icon: FileText },
  { id: 3, label: 'Documents',    icon: FolderOpen },
  { id: 4, label: 'Accès',        icon: Smartphone },
  { id: 5, label: 'Confirmation', icon: Flag },
]

const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
const lbl = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'

interface Establishment { id: string; name: string; city?: string }
interface Position { id: string; name: string; color: string }

export default function EmployeeOnboarding() {
  const { id } = useParams<{ id: string }>()
  const nav = useNavigate()
  const [step, setStep] = useState(1)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [createdId, setCreatedId] = useState<string | null>(id || null)
  const [establishments, setEstablishments] = useState<Establishment[]>([])
  const [positions, setPositions] = useState<Position[]>([])

  // Form data per step
  const [identity, setIdentity] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    birthDate: '', birthPlace: '', nationality: 'Française',
    gender: 'M'
  })
  const [contract, setContract] = useState({
    contractType: 'CDI', weeklyHours: 35, hourlyRate: 12.063,
    conventionLevel: 'E1', establishmentId: '', positionId: '',
    contractStartDate: '', trialPeriodMonths: 2,
    matricule: ''
  })
  const [access, setAccess] = useState({ badgePin: '', sendAppInvite: false })
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set())

  useEffect(() => {
    api.get('/establishments').then(r => setEstablishments(r.data))
    api.get('/positions').then(r => setPositions(r.data)).catch(() => {})
    // If editing existing employee, load data
    if (id) {
      api.get(`/employees/${id}`).then(r => {
        const e = r.data
        setIdentity(prev => ({
          ...prev,
          firstName: e.firstName || '',
          lastName: e.lastName !== '-' ? (e.lastName || '') : '',
          email: e.email || '',
          phone: e.phone || '',
          birthDate: e.birthDate || '',
          birthPlace: e.birthPlace || '',
          nationality: e.nationality || 'Française',
          gender: e.gender || 'M'
        }))
        setContract(prev => ({
          ...prev,
          contractType: e.contractType || 'CDI',
          weeklyHours: e.weeklyHours || 35,
          hourlyRate: e.hourlyRate || 12.063,
          conventionLevel: e.conventionLevel || 'E1',
          establishmentId: e.establishmentId || '',
          positionId: e.positionId || '',
          contractStartDate: e.contractStartDate || '',
          matricule: e.matricule || ''
        }))
        setAccess(prev => ({ ...prev, badgePin: e.badgePin || '' }))
      }).catch(() => {})
    }
  }, [id])

  async function saveStep1() {
    if (!identity.firstName.trim()) { setError('Le prénom est obligatoire'); return false }
    setError('')
    setSaving(true)
    try {
      if (!createdId) {
        const res = await api.post('/employees', {
          firstName: identity.firstName,
          lastName: identity.lastName || '-',
          email: identity.email || null,
          phone: identity.phone || null,
          birthDate: identity.birthDate || null,
          birthPlace: identity.birthPlace || null,
          nationality: identity.nationality,
          gender: identity.gender,
          contractType: 'CDI', weeklyHours: 35, hourlyRate: 12.063,
          conventionLevel: 'E1',
          establishmentId: establishments[0]?.id || '',
          status: 'inactive'
        })
        setCreatedId(res.data.id)
      } else {
        await api.put(`/employees/${createdId}`, {
          firstName: identity.firstName,
          lastName: identity.lastName || '-',
          email: identity.email || null,
          phone: identity.phone || null,
          birthDate: identity.birthDate || null,
          birthPlace: identity.birthPlace || null,
          nationality: identity.nationality,
          gender: identity.gender
        })
      }
      setCompletedSteps(s => new Set([...s, 1]))
      return true
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erreur lors de la sauvegarde')
      return false
    } finally { setSaving(false) }
  }

  async function saveStep2() {
    if (!contract.establishmentId) { setError('Sélectionnez un établissement'); return false }
    if (!contract.contractStartDate) { setError('La date d\'entrée est obligatoire'); return false }
    setError('')
    setSaving(true)
    try {
      await api.put(`/employees/${createdId}`, {
        contractType: contract.contractType,
        weeklyHours: Number(contract.weeklyHours),
        hourlyRate: Number(contract.hourlyRate),
        conventionLevel: contract.conventionLevel,
        establishmentId: contract.establishmentId,
        positionId: contract.positionId || null,
        contractStartDate: contract.contractStartDate,
        trialPeriodMonths: Number(contract.trialPeriodMonths),
        matricule: contract.matricule || null
      })
      setCompletedSteps(s => new Set([...s, 2]))
      return true
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erreur lors de la sauvegarde')
      return false
    } finally { setSaving(false) }
  }

  async function saveStep4() {
    if (access.badgePin && (access.badgePin.length < 4 || !/^\d+$/.test(access.badgePin))) {
      setError('Le PIN doit contenir au moins 4 chiffres'); return false
    }
    setError('')
    if (!access.badgePin) { setCompletedSteps(s => new Set([...s, 4])); return true }
    setSaving(true)
    try {
      await api.put(`/employees/${createdId}`, { badgePin: access.badgePin })
      setCompletedSteps(s => new Set([...s, 4]))
      return true
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erreur')
      return false
    } finally { setSaving(false) }
  }

  async function activate() {
    setSaving(true)
    try {
      await api.put(`/employees/${createdId}`, { status: 'active' })
      setCompletedSteps(s => new Set([...s, 5]))
      nav(`/employees/${createdId}`)
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erreur')
    } finally { setSaving(false) }
  }

  async function goNext() {
    setError('')
    let ok = true
    if (step === 1) ok = await saveStep1()
    if (step === 2) ok = await saveStep2()
    if (step === 3) { setCompletedSteps(s => new Set([...s, 3])); ok = true }
    if (step === 4) ok = await saveStep4()
    if (ok) setStep(s => s + 1)
  }

  function goPrev() { setError(''); setStep(s => s - 1) }

  const canGoNext = step < 5
  const isLastContentStep = step === 4

  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Embauche d'un nouveau salarié</h1>
        <p className="text-sm text-gray-500 mt-1">Suivez les étapes pour configurer le dossier complet</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center mb-8">
        {STEPS.map((s, i) => {
          const Icon = s.icon
          const isActive = step === s.id
          const isDone = completedSteps.has(s.id)
          return (
            <div key={s.id} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <button
                  onClick={() => { if (isDone || s.id <= step) setStep(s.id) }}
                  disabled={!isDone && s.id > step}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    isDone ? 'bg-green-500 text-white' :
                    isActive ? 'bg-blue-600 text-white' :
                    'bg-gray-200 text-gray-400'
                  } disabled:cursor-not-allowed`}
                >
                  {isDone ? <CheckCircle size={18} /> : <Icon size={18} />}
                </button>
                <span className={`text-xs mt-1.5 font-medium ${isActive ? 'text-blue-600' : isDone ? 'text-green-600' : 'text-gray-400'}`}>
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 mb-5 ${completedSteps.has(s.id) ? 'bg-green-400' : 'bg-gray-200'}`} />
              )}
            </div>
          )
        })}
      </div>

      {/* Content card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">

        {/* Step 1: Identité */}
        {step === 1 && (
          <div className="space-y-4">
            <h2 className="font-semibold text-gray-800 text-lg">Informations personnelles</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Prénom *</label>
                <input className={inp} value={identity.firstName} onChange={e => setIdentity(v => ({ ...v, firstName: e.target.value }))} placeholder="Marie" />
              </div>
              <div>
                <label className={lbl}>Nom</label>
                <input className={inp} value={identity.lastName} onChange={e => setIdentity(v => ({ ...v, lastName: e.target.value }))} placeholder="Dupont" />
              </div>
              <div>
                <label className={lbl}>Email professionnel</label>
                <input type="email" className={inp} value={identity.email} onChange={e => setIdentity(v => ({ ...v, email: e.target.value }))} placeholder="marie.dupont@société.fr" />
              </div>
              <div>
                <label className={lbl}>Téléphone</label>
                <input className={inp} value={identity.phone} onChange={e => setIdentity(v => ({ ...v, phone: e.target.value }))} placeholder="06 00 00 00 00" />
              </div>
              <div>
                <label className={lbl}>Date de naissance</label>
                <input type="date" className={inp} value={identity.birthDate} onChange={e => setIdentity(v => ({ ...v, birthDate: e.target.value }))} />
              </div>
              <div>
                <label className={lbl}>Lieu de naissance</label>
                <input className={inp} value={identity.birthPlace} onChange={e => setIdentity(v => ({ ...v, birthPlace: e.target.value }))} placeholder="Paris (75)" />
              </div>
              <div>
                <label className={lbl}>Nationalité</label>
                <input className={inp} value={identity.nationality} onChange={e => setIdentity(v => ({ ...v, nationality: e.target.value }))} />
              </div>
              <div>
                <label className={lbl}>Genre</label>
                <select className={inp} value={identity.gender} onChange={e => setIdentity(v => ({ ...v, gender: e.target.value }))}>
                  <option value="M">Masculin</option>
                  <option value="F">Féminin</option>
                </select>
              </div>
            </div>
            <p className="text-xs text-gray-400">Les informations complémentaires (N° SS, RIB, DPAE…) seront à saisir dans la fiche salarié après création.</p>
          </div>
        )}

        {/* Step 2: Contrat */}
        {step === 2 && (
          <div className="space-y-4">
            <h2 className="font-semibold text-gray-800 text-lg">Contrat & affectation</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Type de contrat *</label>
                <select className={inp} value={contract.contractType} onChange={e => setContract(v => ({ ...v, contractType: e.target.value }))}>
                  {CONTRACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Établissement *</label>
                <select className={inp} value={contract.establishmentId} onChange={e => setContract(v => ({ ...v, establishmentId: e.target.value }))}>
                  <option value="">Choisir...</option>
                  {establishments.map(e => <option key={e.id} value={e.id}>{e.name}{e.city ? ` — ${e.city}` : ''}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Poste</label>
                <select className={inp} value={contract.positionId} onChange={e => setContract(v => ({ ...v, positionId: e.target.value }))}>
                  <option value="">Aucun poste</option>
                  {positions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Niveau convention (IDCC)</label>
                <select className={inp} value={contract.conventionLevel} onChange={e => {
                  const lvl = CCN_LEVELS.find(l => l.value === e.target.value)
                  setContract(v => ({ ...v, conventionLevel: e.target.value, hourlyRate: lvl?.rate || v.hourlyRate }))
                }}>
                  {CCN_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Heures hebdomadaires *</label>
                <input type="number" className={inp} value={contract.weeklyHours} min={1} max={48} onChange={e => setContract(v => ({ ...v, weeklyHours: Number(e.target.value) }))} />
              </div>
              <div>
                <label className={lbl}>Taux horaire brut (€) *</label>
                <input type="number" className={inp} value={contract.hourlyRate} min={11} step={0.001} onChange={e => setContract(v => ({ ...v, hourlyRate: Number(e.target.value) }))} />
              </div>
              <div>
                <label className={lbl}>Date d'entrée *</label>
                <input type="date" className={inp} value={contract.contractStartDate} onChange={e => setContract(v => ({ ...v, contractStartDate: e.target.value }))} />
              </div>
              <div>
                <label className={lbl}>Période d'essai (mois)</label>
                <input type="number" className={inp} value={contract.trialPeriodMonths} min={0} max={12} onChange={e => setContract(v => ({ ...v, trialPeriodMonths: Number(e.target.value) }))} />
              </div>
              <div className="col-span-2">
                <label className={lbl}>Matricule paie</label>
                <input className={inp} value={contract.matricule} onChange={e => setContract(v => ({ ...v, matricule: e.target.value }))} placeholder="Auto-généré si vide" />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Documents */}
        {step === 3 && (
          <div className="space-y-4">
            <h2 className="font-semibold text-gray-800 text-lg">Documents à recueillir</h2>
            <p className="text-sm text-gray-600">
              Les documents seront ajoutés directement dans la fiche salarié (onglet Documents).
              Voici la liste des pièces à collecter lors de l'embauche :
            </p>
            <div className="space-y-2">
              {[
                { label: 'Pièce d\'identité (carte nationale ou passeport)', required: true },
                { label: 'Titre de séjour / autorisation de travail', required: false, note: 'si ressortissant étranger' },
                { label: 'Carte vitale (ou attestation Sécu)', required: true },
                { label: 'RIB (relevé d\'identité bancaire)', required: true },
                { label: 'Justificatif de domicile (-3 mois)', required: true },
                { label: 'Diplômes / certifications', required: false },
                { label: 'Attestation employeur précédent', required: false },
                { label: 'Contrat signé', required: true },
              ].map(doc => (
                <div key={doc.label} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${doc.required ? 'bg-blue-100' : 'bg-gray-100'}`}>
                    {doc.required ? <span className="text-blue-600 text-xs font-bold">!</span> : <span className="text-gray-400 text-xs">·</span>}
                  </div>
                  <div>
                    <span className="text-sm text-gray-700">{doc.label}</span>
                    {doc.note && <span className="text-xs text-gray-400 ml-2">({doc.note})</span>}
                    {doc.required && <span className="text-xs text-blue-600 ml-2 font-medium">Obligatoire</span>}
                  </div>
                </div>
              ))}
            </div>
            {createdId && (
              <a
                href={`/employees/${createdId}?tab=documents`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline mt-2"
              >
                <ExternalLink size={14} /> Ouvrir la fiche pour téléverser les documents
              </a>
            )}
            <p className="text-xs text-gray-400">Vous pouvez passer cette étape et ajouter les documents plus tard.</p>
          </div>
        )}

        {/* Step 4: Accès */}
        {step === 4 && (
          <div className="space-y-5">
            <h2 className="font-semibold text-gray-800 text-lg">Badge & accès mobile</h2>

            {/* Badge PIN */}
            <div className="p-4 bg-gray-50 rounded-xl">
              <h3 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                <span className="text-lg">📟</span> PIN badgeuse tablette
              </h3>
              <div className="max-w-xs">
                <label className={lbl}>Code PIN (4–6 chiffres)</label>
                <input
                  type="text" inputMode="numeric" pattern="[0-9]*"
                  maxLength={6} className={inp}
                  value={access.badgePin}
                  onChange={e => setAccess(v => ({ ...v, badgePin: e.target.value.replace(/\D/g, '') }))}
                  placeholder="Ex: 1234"
                />
                <p className="text-xs text-gray-400 mt-1">Le salarié utilisera ce code pour pointer sur la tablette kiosque.</p>
              </div>
            </div>

            {/* Mobile app */}
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
              <h3 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                <span className="text-lg">📱</span> Application mobile salarié
              </h3>
              <p className="text-sm text-blue-700 mb-3">
                Le salarié peut accéder à l'application mobile via <strong>son navigateur</strong> sur smartphone.
              </p>
              <div className="bg-white rounded-lg p-3 space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">URL d'accès</span>
                  <span className="font-mono text-blue-600 text-xs">/app/login</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Établissement</span>
                  <span className="text-gray-800 text-xs">
                    {establishments.find(e => e.id === contract.establishmentId)?.name || '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Authentification</span>
                  <span className="text-gray-800 text-xs">PIN badgeuse</span>
                </div>
              </div>
              <p className="text-xs text-blue-500 mt-2">
                Le salarié se connectera en sélectionnant son établissement et son nom, puis en saisissant son PIN.
              </p>
            </div>
          </div>
        )}

        {/* Step 5: Confirmation */}
        {step === 5 && (
          <div className="space-y-5">
            <h2 className="font-semibold text-gray-800 text-lg">Récapitulatif & activation</h2>

            {/* Summary */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">
                  {identity.firstName[0] || '?'}
                </div>
                <div>
                  <div className="font-semibold text-gray-800">{identity.firstName} {identity.lastName}</div>
                  <div className="text-sm text-gray-500">{contract.contractType} · {contract.weeklyHours}h/sem</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm pt-2 border-t border-gray-200">
                <div><span className="text-gray-500">Taux horaire :</span> <span className="font-medium">{Number(contract.hourlyRate).toFixed(3)} €</span></div>
                <div><span className="text-gray-500">Convention :</span> <span className="font-medium">{contract.conventionLevel}</span></div>
                <div><span className="text-gray-500">Établissement :</span> <span className="font-medium">{establishments.find(e => e.id === contract.establishmentId)?.name || '—'}</span></div>
                <div><span className="text-gray-500">Poste :</span> <span className="font-medium">{positions.find(p => p.id === contract.positionId)?.name || 'Non défini'}</span></div>
                <div><span className="text-gray-500">Date d'entrée :</span> <span className="font-medium">{contract.contractStartDate || '—'}</span></div>
                <div><span className="text-gray-500">Badge PIN :</span> <span className="font-medium">{access.badgePin ? '••••' : 'Non défini'}</span></div>
              </div>
            </div>

            {/* Checklist */}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-700">Liste de contrôle :</p>
              {[
                { label: 'Informations personnelles', done: completedSteps.has(1) },
                { label: 'Contrat & affectation', done: completedSteps.has(2) },
                { label: 'Documents (vérification)', done: completedSteps.has(3) },
                { label: 'Badge & accès mobile', done: completedSteps.has(4) },
              ].map(item => (
                <div key={item.label} className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${item.done ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                  {item.done
                    ? <CheckCircle size={15} className="text-green-500 flex-shrink-0" />
                    : <AlertCircle size={15} className="text-amber-500 flex-shrink-0" />}
                  {item.label}
                  {!item.done && <span className="ml-auto text-xs text-amber-500">À compléter</span>}
                </div>
              ))}
            </div>

            <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
              <p className="text-sm text-blue-800 font-medium mb-1">Activation du compte</p>
              <p className="text-sm text-blue-600">
                Cliquer sur <strong>Activer le salarié</strong> passera son statut à <em>Actif</em> :
                il apparaîtra dans le planning, pourra pointer et accéder à l'application mobile.
              </p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mt-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            <AlertCircle size={15} className="flex-shrink-0" />
            {error}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6">
        <button
          onClick={step === 1 ? () => nav('/employees') : goPrev}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
        >
          <ChevronLeft size={16} />
          {step === 1 ? 'Annuler' : 'Retour'}
        </button>

        <span className="text-xs text-gray-400">Étape {step} sur {STEPS.length}</span>

        {step < 5 ? (
          <button
            onClick={goNext}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
          >
            {saving ? 'Sauvegarde...' : (step === 3 ? 'Continuer' : 'Suivant')}
            <ChevronRight size={16} />
          </button>
        ) : (
          <button
            onClick={activate}
            disabled={saving || !completedSteps.has(1) || !completedSteps.has(2)}
            className="flex items-center gap-2 px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
          >
            {saving ? 'Activation...' : 'Activer le salarié'}
            <CheckCircle size={16} />
          </button>
        )}
      </div>
    </div>
  )
}
